import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ShoppingCart, TrendingUp, Wallet, Bell, ChevronRight, Package, 
  IndianRupee, Sparkles, Calendar, Clock, AlertTriangle, 
  ArrowUpCircle, ArrowDownCircle, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingOrders: 0,
    todayCashReport: null,
    unreadNotifications: 0,
    recentOrders: []
  });
  const [wallet, setWallet] = useState({
    balance: 0,
    low_balance_warning: false,
    threshold: 500
  });
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    fetchDashboardData();
    
    // Set greeting based on time
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 17) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [ordersRes, notificationsRes, cashReportRes, walletRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/cash-reports/${new Date().toISOString().split('T')[0]}`),
        axios.get(`${API}/wallet/balance`),
        axios.get(`${API}/wallet/transactions`)
      ]);

      const pendingOrders = ordersRes.data.filter(o => o.payment_status === "pending").length;
      const unreadNotifications = notificationsRes.data.filter(n => !n.is_read).length;

      setStats({
        pendingOrders,
        todayCashReport: cashReportRes.data,
        unreadNotifications,
        recentOrders: ordersRes.data.slice(0, 3)
      });
      
      setWallet(walletRes.data);
      setTransactions(transactionsRes.data.slice(0, 5)); // Last 5 transactions
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Order Supplies",
      description: "Browse and order from product catalog",
      icon: ShoppingCart,
      gradient: "from-[#F2337B] to-[#FF6B9D]",
      shadowColor: "rgba(242, 51, 123, 0.3)",
      path: "/ordering"
    },
    {
      title: "View Sales",
      description: "Check your POS sales data",
      icon: TrendingUp,
      gradient: "from-[#087DDF] to-[#5DADE2]",
      shadowColor: "rgba(8, 125, 223, 0.3)",
      path: "/sales"
    },
    {
      title: "Report Cash Sales",
      description: "Enter today's cash collection",
      icon: Wallet,
      gradient: "from-[#F6D003] to-[#FFE066]",
      shadowColor: "rgba(246, 208, 3, 0.3)",
      textDark: true,
      path: "/cash-reporting"
    }
  ];

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit':
        return <ArrowUpCircle className="w-5 h-5 text-green-500" />;
      case 'debit':
        return <ArrowDownCircle className="w-5 h-5 text-red-500" />;
      case 'refund':
        return <RefreshCw className="w-5 h-5 text-blue-500" />;
      default:
        return <Wallet className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="flex min-h-screen bg-background lotus-bg overflow-x-hidden">
      <Sidebar user={user} activePath="/dashboard" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64 overflow-x-hidden max-w-full">
        {/* Colorful Header */}
        <div className="mb-6 sm:mb-8 relative">
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br from-[#F2337B]/10 to-[#F6D003]/10 rounded-full blur-2xl"></div>
          <div className="relative">
            <p className="text-muted-foreground mb-1 flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-[#F6D003]" />
              {greeting}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-1 sm:mb-2">
              <span className="text-gradient-pink-yellow">{user.name?.split(' ')[0] || 'Franchisee'}</span>
            </h1>
            <p className="text-muted-foreground flex items-center gap-2 text-xs sm:text-sm">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Low Balance Warning */}
        {wallet.low_balance_warning && (
          <Alert className="mb-6 border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Low wallet balance!</strong> Your balance is below ₹{wallet.threshold}. Please contact admin to add funds.
            </AlertDescription>
          </Alert>
        )}

        {/* Wallet Balance Card - Prominent */}
        <Card className="border-0 mb-6 sm:mb-8 overflow-hidden shadow-xl" data-testid="wallet-card">
          <CardContent className="p-0">
            <div className="bg-gradient-to-r from-[#2ECC71] to-[#27AE60] p-4 sm:p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm mb-1">Wallet Balance</p>
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-heading flex items-center">
                    <IndianRupee className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                    {wallet.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-white/70 text-xs mt-2">Available for ordering supplies</p>
                </div>
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <Wallet className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
            </div>
            
            {/* Recent Transactions */}
            {transactions.length > 0 && (
              <div className="p-4 sm:p-6 bg-white">
                <h4 className="font-semibold text-sm text-muted-foreground mb-3">Recent Transactions</h4>
                <div className="space-y-2">
                  {transactions.map((txn, index) => (
                    <div key={txn.transaction_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        {getTransactionIcon(txn.type)}
                        <div>
                          <p className="text-sm font-medium capitalize">{txn.type}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-[250px]">
                            {txn.reference}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${txn.type === 'debit' ? 'text-red-500' : 'text-green-500'}`}>
                          {txn.type === 'debit' ? '-' : '+'}₹{txn.amount.toLocaleString('en-IN')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Bal: ₹{txn.balance_after.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions - Colorful Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {quickActions.map((action, index) => (
            <Card 
              key={action.title}
              data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="border-0 overflow-hidden cursor-pointer group animate-fade-in"
              style={{ 
                animationDelay: `${index * 0.1}s`,
                boxShadow: `0 10px 30px -10px ${action.shadowColor}`
              }}
              onClick={() => navigate(action.path)}
            >
              <CardContent className={`p-4 sm:p-5 lg:p-6 bg-gradient-to-br ${action.gradient} text-white relative overflow-hidden`}>
                {/* Decorative circles */}
                <div className="absolute -right-6 -top-6 w-20 sm:w-24 h-20 sm:h-24 bg-white/10 rounded-full"></div>
                <div className="absolute -right-2 -bottom-8 w-24 sm:w-32 h-24 sm:h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className={`w-10 h-10 sm:w-12 lg:w-14 sm:h-12 lg:h-14 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-3 lg:mb-4 group-hover:scale-110 transition-transform`}>
                    <action.icon className={`w-5 h-5 sm:w-6 lg:w-7 sm:h-6 lg:h-7 ${action.textDark ? 'text-gray-800' : 'text-white'}`} />
                  </div>
                  <h3 className={`font-semibold text-base sm:text-lg lg:text-xl mb-0.5 sm:mb-1 ${action.textDark ? 'text-gray-800' : 'text-white'}`}>
                    {action.title}
                  </h3>
                  <p className={`text-xs sm:text-sm ${action.textDark ? 'text-gray-700' : 'text-white/80'} line-clamp-1 sm:line-clamp-none`}>
                    {action.description}
                  </p>
                  <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 mt-2 sm:mt-3 lg:mt-4 group-hover:translate-x-1 transition-transform ${action.textDark ? 'text-gray-700' : 'text-white/70'}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats Row - Colorful */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <Card className="border-0 stat-card-pink rounded-xl sm:rounded-2xl">
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">Total Orders</p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-heading mt-0.5 sm:mt-1 text-[#F2337B]">{stats.recentOrders.length}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 lg:w-14 sm:h-12 lg:h-14 bg-gradient-to-br from-[#F2337B] to-[#FF6B9D] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Package className="w-5 h-5 sm:w-6 lg:w-7 sm:h-6 lg:h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 stat-card-yellow rounded-xl sm:rounded-2xl">
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">Today's Cash</p>
                  {stats.todayCashReport ? (
                    <p className="text-2xl sm:text-3xl lg:text-4xl font-heading mt-0.5 sm:mt-1 text-[#9A7B00] flex items-center">
                      <IndianRupee className="w-5 h-5 sm:w-6 lg:w-7 sm:h-6 lg:h-7" />
                      <span className="truncate">{stats.todayCashReport.cash_amount.toLocaleString('en-IN')}</span>
                    </p>
                  ) : (
                    <p className="text-lg sm:text-xl lg:text-2xl font-heading mt-0.5 sm:mt-1 text-[#F2337B]">Not Reported</p>
                  )}
                </div>
                <div className="w-10 h-10 sm:w-12 lg:w-14 sm:h-12 lg:h-14 bg-gradient-to-br from-[#F6D003] to-[#FFE066] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <TrendingUp className="w-5 h-5 sm:w-6 lg:w-7 sm:h-6 lg:h-7 text-gray-800" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 stat-card-blue rounded-xl sm:rounded-2xl col-span-2 lg:col-span-1">
            <CardContent className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">Notifications</p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-heading mt-0.5 sm:mt-1 text-[#087DDF]">{stats.unreadNotifications}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 lg:w-14 sm:h-12 lg:h-14 bg-gradient-to-br from-[#087DDF] to-[#5DADE2] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg relative flex-shrink-0">
                  <Bell className="w-5 h-5 sm:w-6 lg:w-7 sm:h-6 lg:h-7 text-white" />
                  {stats.unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#F2337B] text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse-gentle">
                      {stats.unreadNotifications}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Orders - Enhanced */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden" data-testid="recent-orders-card">
          <CardHeader className="bg-gradient-to-r from-[#F2337B]/5 to-[#F6D003]/5 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#F2337B]" />
                Recent Orders
              </CardTitle>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/orders')}
                className="text-[#F2337B] hover:text-[#F2337B] hover:bg-[#F2337B]/10 rounded-full"
                data-testid="view-all-orders-btn"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : stats.recentOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-[#F2337B]/10 to-[#F6D003]/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <Package className="w-10 h-10 text-[#F2337B]" />
                </div>
                <h3 className="font-heading text-xl mb-2">No Orders Yet</h3>
                <p className="text-muted-foreground mb-4">Start ordering supplies for your franchise!</p>
                <Button 
                  className="rounded-full bg-gradient-to-r from-[#F2337B] to-[#FF6B9D] hover:opacity-90 shadow-lg"
                  onClick={() => navigate('/ordering')}
                  data-testid="start-ordering-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Start Ordering
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {stats.recentOrders.map((order, index) => (
                  <div 
                    key={order.order_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow animate-fade-in gap-3"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`order-item-${order.order_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#087DDF]/10 to-[#087DDF]/5 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Package className="w-6 h-6 text-[#087DDF]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{order.order_id}</p>
                        <p className="text-sm text-muted-foreground">
                          {order.items.length} items • {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                      <p className="font-heading text-xl flex items-center text-[#087DDF]">
                        <IndianRupee className="w-4 h-4" />
                        {order.total_amount.toLocaleString('en-IN')}
                      </p>
                      <Badge className="bg-gradient-to-r from-[#2ECC71]/20 to-[#2ECC71]/10 text-[#27AE60] rounded-full">
                        ✓ Paid (Wallet)
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
